import { db, storage } from './config';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
  getDocs,
  limit as fsLimit,
  serverTimestamp,
  getCountFromServer,
  addDoc,
  arrayUnion,
  arrayRemove,
  type Query,
  type DocumentData,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import type { Patient, PatientIdDocument, PatientInsurer } from '@/types/models';
import { encryptData, decryptData, hashData, normalizeForSearch } from '@/utils/crypto';
import { extractBirthDayMonthUTC } from '@/utils/birthDate';
import { normalizePhoneDigits } from '@/utils/phone';

/**
 * Tamanho do prefixo do CPF indexado em texto claro pra suportar busca
 * por primeiros digitos. 4 digitos nao identificam ninguem (10k prefixos
 * possiveis) e dao boa cardinalidade pra queries.
 */
const CPF_PREFIX_LENGTH = 4;

function extractCpfPrefix(cpf: string | undefined): string | undefined {
  if (!cpf) return undefined;
  const digits = cpf.replace(/\D/g, '');
  if (digits.length < CPF_PREFIX_LENGTH) return undefined;
  return digits.slice(0, CPF_PREFIX_LENGTH);
}

/** Converte epoch ms (birthDate, UTC midnight) em "MM-DD" pra indexar aniversariantes. */
function extractBirthDayMonth(birthDate: number | undefined | null): string | undefined {
  return extractBirthDayMonthUTC(birthDate);
}

/** Só pra montar a query de busca — devolve '' quando não há dígitos.
 *  A derivação do campo persistido usa `normalizePhoneDigits` de
 *  `@/utils/phone`, que é mais rigorosa (DDI, segundo número, exige DDD). */
function searchPhoneDigits(phone: string | undefined): string {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

/** Ids já curados nesta sessão — evita reescrever o mesmo doc em loop. */
const healedNumbers = new Set<string>();

/**
 * Conserta o `patientNumber` de pacientes legados cujo CPF foi cadastrado
 * DEPOIS (o número tinha ficado 'P-######', então a exibição mostra "Sem CPF"
 * apesar de o CPF existir). Para cada paciente inconsistente, descriptografa o
 * CPF; se válido (11 dígitos), corrige em memória — pra exibição imediata — e
 * grava no Firestore em background (uma vez por id por sessão). Idempotente e
 * best-effort: usado por searchPatients/getPatientsByIds, então TODO seletor de
 * paciente passa a exibir o CPF certo sem tratar isso caso a caso.
 */
async function healPatientNumbers(clinicId: string, patients: Patient[]): Promise<Patient[]> {
  return Promise.all(
    patients.map(async (p) => {
      if (/^\d{11}$/.test(p.patientNumber || '') || !p.cpf) return p;
      let plain: string;
      try {
        plain = (await decryptData(p.cpf)).replace(/\D/g, '');
      } catch {
        return p;
      }
      if (!/^\d{11}$/.test(plain)) return p;
      if (!healedNumbers.has(p.id)) {
        healedNumbers.add(p.id);
        const updates: Record<string, unknown> = { patientNumber: plain };
        if (!p.cpfHash) updates.cpfHash = await hashData(plain);
        if (!p.cpfPrefix) updates.cpfPrefix = plain.slice(0, CPF_PREFIX_LENGTH);
        // fire-and-forget: não atrasa a busca; se falhar, tenta de novo na próxima sessão
        updateDoc(doc(db, 'clinics', clinicId, 'patients', p.id), updates).catch(() => {
          healedNumbers.delete(p.id);
        });
      }
      return { ...p, patientNumber: plain };
    }),
  );
}

export const PatientService = {
  async createPatient(
    clinicId: string,
    data: Omit<Patient, 'id' | 'clinicId' | 'createdAt' | 'updatedAt' | 'isDeleted' | 'patientNumber'>,
  ) {
    const newDocRef = doc(collection(db, 'clinics', clinicId, 'patients'));

    // Generate patientNumber
    let patientNumber: string;
    if (data.cpf) {
      patientNumber = data.cpf.replace(/\D/g, '');
    } else {
      const countSnap = await getCountFromServer(collection(db, 'clinics', clinicId, 'patients'));
      const seq = (countSnap.data().count + 1).toString().padStart(6, '0');
      patientNumber = `P-${seq}`;
    }

    // Encrypt CPF and RG, compute hash for search
    const cpfEncrypted = data.cpf ? await encryptData(data.cpf) : undefined;
    const cpfHash = data.cpf ? await hashData(data.cpf) : undefined;
    const cpfPrefix = extractCpfPrefix(data.cpf);
    const rgEncrypted = data.rg ? await encryptData(data.rg) : undefined;

    // Normalize name for search
    const nameNormalized = normalizeForSearch(data.name);
    const birthDayMonth = extractBirthDayMonth(data.birthDate);
    // Telefone canônico (só dígitos, com DDD) pro vínculo do portal e pro
    // WhatsApp. `undefined` quando não dá pra recuperar — o filtro de
    // undefined logo abaixo faz o campo simplesmente não existir, em vez de
    // gravar '' (que faria todas as fichas sem telefone casarem entre si).
    const phoneDigits = normalizePhoneDigits(data.phone);

    // Build patient data, filtering out undefined values (Firestore rejects them)
    const rawData: Record<string, unknown> = {
      ...data,
      id: newDocRef.id,
      clinicId,
      patientNumber,
      cpf: cpfEncrypted,
      cpfHash,
      cpfPrefix,
      rg: rgEncrypted,
      nameNormalized,
      birthDayMonth,
      phoneDigits,
      registrationStatus: data.registrationStatus ?? 'MINIMAL',
      isDeleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    // Remove undefined fields
    const patientData = Object.fromEntries(
      Object.entries(rawData).filter(([, v]) => v !== undefined),
    );

    await setDoc(newDocRef, patientData);

    // Record LGPD consent if provided
    if (data.lgpdConsentDate) {
      await PatientService.recordLgpdConsent(clinicId, newDocRef.id, data.createdBy, '1.0');
    }

    return patientData;
  },

  async updatePatient(clinicId: string, patientId: string, data: Partial<Patient>) {
    const docRef = doc(db, 'clinics', clinicId, 'patients', patientId);

    const updates: Record<string, unknown> = { ...data, updatedAt: serverTimestamp() };

    // Re-encrypt if CPF/RG changed
    if (data.cpf) {
      updates.cpf = await encryptData(data.cpf);
      updates.cpfHash = await hashData(data.cpf);
      const prefix = extractCpfPrefix(data.cpf);
      if (prefix) updates.cpfPrefix = prefix;
      // Mantém patientNumber em sincronia com o CPF. Sem isso, um paciente
      // criado SEM CPF (patientNumber = 'P-######') que recebe o CPF numa
      // edição continua exibido como "Sem CPF" na busca/cabeçalho — porque a
      // exibição usa patientNumber, não o cpf cifrado.
      const digits = data.cpf.replace(/\D/g, '');
      if (/^\d{11}$/.test(digits)) updates.patientNumber = digits;
    }
    if (data.rg) {
      updates.rg = await encryptData(data.rg);
    }
    // Mantém `phoneDigits` em sincronia sempre que o telefone é tocado.
    // `null` (e não undefined) porque updateDoc rejeita undefined — e o
    // null é o que efetivamente limpa o campo quando o telefone novo é
    // inutilizável (ex.: paciente apagou o número).
    if (data.phone !== undefined) {
      updates.phoneDigits = normalizePhoneDigits(data.phone) ?? null;
    }
    if (data.name) {
      updates.nameNormalized = normalizeForSearch(data.name);
    }
    if (data.birthDate !== undefined) {
      const dm = extractBirthDayMonth(data.birthDate);
      updates.birthDayMonth = dm ?? null;
    }

    await updateDoc(docRef, updates);
  },

  /**
   * Garante que o convênio esteja cadastrado na ficha do paciente, mantendo a
   * ficha em dia automaticamente quando um agendamento recebe um convênio
   * (assim o selo da ficha e o do agendamento não divergem). Cria a carteirinha
   * sem número (o staff completa depois no Cadastro) e lê a versão mais recente
   * antes de gravar pra não sobrescrever carteirinhas adicionadas em paralelo.
   *
   * `opts.promote`:
   *   - true (ação deliberada: criar/trocar convênio do agendamento) → o
   *     convênio vira o PRINCIPAL (insurers[0]), pra o cabeçalho da ficha
   *     refletir o agendamento atual. Se já existia, é MOVIDO pro topo
   *     preservando cardNumber/plan/validUntil (nunca recria com cartão vazio).
   *   - false (abertura passiva de agendamento) → só ADICIONA se faltar (no
   *     fim), sem reordenar — evita "churn" de embaralhar a ficha só por abrir
   *     agendamentos antigos com convênios diferentes.
   *
   * Limpeza preguiçosa: "Particular" não é um convênio de verdade (artefato da
   * migração do HiDoctor — ser particular = não ter convênio). Ao garantir um
   * convênio real, removemos qualquer carteirinha cujo convênio se chame
   * "Particular" — ter convênio e ser particular ao mesmo tempo é contraditório.
   *
   * A comparação posicional final detecta tanto mudança de conteúdo quanto de
   * ORDEM, então uma promoção que não muda nada (já era insurers[0]) é no-op.
   * Retorna o novo array se algo mudou (pra o chamador atualizar o estado local)
   * ou null se nada mudou / paciente inexistente.
   */
  async ensureInsurerOnFile(
    clinicId: string,
    patientId: string,
    insurerId: string,
    opts: { promote?: boolean } = {},
  ): Promise<PatientInsurer[] | null> {
    if (!insurerId) return null;
    const promote = opts.promote ?? false;
    const patient = await PatientService.getPatient(clinicId, patientId);
    if (!patient) return null;
    const current = patient.insurers ?? [];

    // ids de convênios chamados "Particular" (pra remover na limpeza preguiçosa).
    const insSnap = await getDocs(collection(db, 'clinics', clinicId, 'insurers'));
    const particularIds = new Set(
      insSnap.docs
        .filter((d) => String((d.data() as { name?: string }).name ?? '').trim().toLowerCase() === 'particular')
        .map((d) => d.id),
    );

    const filtered = current.filter((i) => !particularIds.has(i.insurerId));
    // Registro existente (com cartão/plano/validade já preenchidos, se houver).
    const existing = filtered.find((i) => i.insurerId === insurerId);

    let next: PatientInsurer[];
    if (promote) {
      // Move (ou cria) o convênio pro topo, preservando os dados do existente.
      const rest = filtered.filter((i) => i.insurerId !== insurerId);
      next = [existing ?? { insurerId, cardNumber: '' }, ...rest];
    } else {
      // Adiciona só se faltar; não reordena.
      next = existing ? filtered : [...filtered, { insurerId, cardNumber: '' }];
    }

    const unchanged =
      next.length === current.length &&
      next.every((i, idx) => i.insurerId === current[idx].insurerId);
    if (unchanged) return null;

    await PatientService.updatePatient(clinicId, patientId, { insurers: next });
    return next;
  },

  /**
   * T079: Soft delete with LGPD anonymization.
   * Per FR-126 + FR-132: marks as deleted, preserves structure for CFM compliance.
   */
  async softDeletePatient(clinicId: string, patientId: string, deletedBy: string) {
    const docRef = doc(db, 'clinics', clinicId, 'patients', patientId);
    await updateDoc(docRef, {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      deletedBy,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * T079: Request LGPD deletion (anonymize personal data, preserve clinical).
   */
  async requestLgpdDeletion(clinicId: string, patientId: string, deletedBy: string) {
    const docRef = doc(db, 'clinics', clinicId, 'patients', patientId);
    await updateDoc(docRef, {
      name: '[ANONIMIZADO LGPD]',
      cpf: null,
      cpfHash: null,
      rg: null,
      email: null,
      address: null,
      photoUrl: null,
      guardians: null,
      phone: '[ANONIMIZADO]',
      // Sem isto o telefone anonimizado continuaria pesquisável e ainda
      // casaria no /identity/link do portal — vazando justamente quem
      // pediu apagamento.
      phoneDigits: null,
      isDeleted: true,
      deletedAt: serverTimestamp(),
      deletedBy,
      lgpdDeletionRequested: true,
      updatedAt: serverTimestamp(),
    });
  },

  async getPatient(clinicId: string, patientId: string) {
    const docRef = doc(db, 'clinics', clinicId, 'patients', patientId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return snap.data() as Patient;
  },

  /**
   * List all active patients for a clinic (for initial load).
   * Evitar usar em telas de listagem: prefira countPatients + listPatientsByLetter
   * para nao carregar a base inteira no mount.
   */
  async listPatients(clinicId: string) {
    const patientsRef = collection(db, 'clinics', clinicId, 'patients');
    const q = query(patientsRef, where('isDeleted', '==', false));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as Patient);
  },

  /**
   * Conta pacientes ativos sem trazer os documentos (rapido, custo 1 read).
   */
  async countPatients(clinicId: string): Promise<number> {
    const patientsRef = collection(db, 'clinics', clinicId, 'patients');
    const q = query(patientsRef, where('isDeleted', '==', false));
    const snap = await getCountFromServer(q);
    return snap.data().count;
  },

  /**
   * Busca pacientes pelos ids (usado pra lista "acessados recentemente").
   * Filtra ids invalidos/deletados e mantem a ordem original.
   */
  async getPatientsByIds(clinicId: string, ids: string[]): Promise<Patient[]> {
    if (!ids.length) return [];
    const unique = Array.from(new Set(ids));
    const results = await Promise.all(
      unique.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, 'clinics', clinicId, 'patients', id));
          if (!snap.exists()) return null;
          const data = snap.data() as Patient;
          return data.isDeleted ? null : data;
        } catch {
          return null;
        }
      }),
    );
    const map = new Map<string, Patient>();
    results.forEach((p) => {
      if (p) map.set(p.id, p);
    });
    // Preserva ordem do array de entrada
    const ordered = ids.map((id) => map.get(id)).filter((p): p is Patient => !!p);
    return healPatientNumbers(clinicId, ordered);
  },

  /**
   * Lista pacientes cujo nome comeca com a letra informada (case/accent insensitive).
   * Usa range query em nameNormalized — requer indice composto
   * (isDeleted ASC, nameNormalized ASC).
   */
  async listPatientsByLetter(clinicId: string, letter: string): Promise<Patient[]> {
    if (!letter) return [];
    const normalized = normalizeForSearch(letter);
    if (!normalized) return [];
    const start = normalized.charAt(0);
    // proximo caractere unicode — incrementa pra fechar a faixa
    const end = String.fromCharCode(start.charCodeAt(0) + 1);
    const patientsRef = collection(db, 'clinics', clinicId, 'patients');
    const q = query(
      patientsRef,
      where('isDeleted', '==', false),
      where('nameNormalized', '>=', start),
      where('nameNormalized', '<', end),
      orderBy('nameNormalized', 'asc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as Patient);
  },

  /**
   * Busca pacientes por um dos modos explicitos:
   *  - 'name'      : prefix match em nameNormalized (min 2 chars)
   *  - 'cpf'       : CPF completo (11 digitos) via cpfHash
   *  - 'birthDate' : data exata (formato ISO yyyy-mm-dd)
   *  - 'tag'       : array-contains na tag informada
   *
   * Requer indices compostos:
   *  (isDeleted, nameNormalized) — name
   *  (isDeleted, birthDate)       — birthDate
   *  (isDeleted, tags array)      — tag
   */
  async searchPatients(
    clinicId: string,
    searchTerm: string,
    mode: 'name' | 'cpf' | 'birthDate' | 'tag' | 'phone' = 'name',
  ): Promise<Patient[]> {
    const out = await this._searchPatientsRaw(clinicId, searchTerm, mode);
    return healPatientNumbers(clinicId, out);
  },

  async _searchPatientsRaw(
    clinicId: string,
    searchTerm: string,
    mode: 'name' | 'cpf' | 'birthDate' | 'tag' | 'phone' = 'name',
  ): Promise<Patient[]> {
    const trimmed = (searchTerm || '').trim();
    if (!trimmed) return [];

    const patientsRef = collection(db, 'clinics', clinicId, 'patients');

    if (mode === 'phone') {
      const digits = searchPhoneDigits(trimmed);
      if (digits.length < 4) return [];
      // Telefones são guardados como dígitos puros. Usa range pra prefixo.
      const q = query(
        patientsRef,
        where('isDeleted', '==', false),
        where('phone', '>=', digits),
        where('phone', '<=', digits + ''),
        orderBy('phone', 'asc'),
        fsLimit(50),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => d.data() as Patient);
    }

    if (mode === 'cpf') {
      const digits = trimmed.replace(/\D/g, '');
      if (digits.length < 2) return [];

      // CPF completo: busca exata por hash
      if (digits.length === 11) {
        const hash = await hashData(digits);
        const q = query(
          patientsRef,
          where('cpfHash', '==', hash),
          where('isDeleted', '==', false),
        );
        const snap = await getDocs(q);
        return snap.docs.map((d) => d.data() as Patient);
      }

      // Prefix parcial: faz range query no cpfPrefix indexado
      const prefixForQuery = digits.slice(0, CPF_PREFIX_LENGTH);
      const start = prefixForQuery.padEnd(CPF_PREFIX_LENGTH, '0');
      const end = prefixForQuery.padEnd(CPF_PREFIX_LENGTH, '9') + '\uf8ff';
      const q = query(
        patientsRef,
        where('isDeleted', '==', false),
        where('cpfPrefix', '>=', start),
        where('cpfPrefix', '<=', end),
        orderBy('cpfPrefix', 'asc'),
      );
      const snap = await getDocs(q);
      const candidates = snap.docs.map((d) => d.data() as Patient);

      // Se usuario digitou <= CPF_PREFIX_LENGTH digitos, a query ja e exata
      if (digits.length <= CPF_PREFIX_LENGTH) return candidates;

      // Caso contrario (5-10 digitos): refina decifrando os CPFs candidatos
      const refined = await Promise.all(
        candidates.map(async (p) => {
          if (!p.cpf) return null;
          try {
            const plain = (await decryptData(p.cpf)).replace(/\D/g, '');
            return plain.startsWith(digits) ? p : null;
          } catch {
            return null;
          }
        }),
      );
      return refined.filter((p): p is Patient => !!p);
    }

    if (mode === 'birthDate') {
      // Aceita yyyy-mm-dd (input type=date) ou dd/mm/yyyy
      let yyyy: number | undefined;
      let mm: number | undefined;
      let dd: number | undefined;
      const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      const br = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (iso) {
        yyyy = Number(iso[1]);
        mm = Number(iso[2]);
        dd = Number(iso[3]);
      } else if (br) {
        dd = Number(br[1]);
        mm = Number(br[2]);
        yyyy = Number(br[3]);
      } else {
        return [];
      }
      if (!yyyy || !mm || !dd) return [];
      // birthDate é salvo como UTC midnight (ver utils/birthDate.ts). Range
      // precisa ser em UTC pra casar com o storage.
      const start = Date.UTC(yyyy, mm - 1, dd, 0, 0, 0, 0);
      const end = Date.UTC(yyyy, mm - 1, dd, 23, 59, 59, 999);
      const q = query(
        patientsRef,
        where('isDeleted', '==', false),
        where('birthDate', '>=', start),
        where('birthDate', '<=', end),
        orderBy('birthDate', 'asc'),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => d.data() as Patient);
    }

    if (mode === 'tag') {
      const q = query(
        patientsRef,
        where('isDeleted', '==', false),
        where('tags', 'array-contains', trimmed),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => d.data() as Patient);
    }

    // mode === 'name' (default)
    if (trimmed.length < 2) return [];
    const prefix = normalizeForSearch(trimmed);
    if (!prefix) return [];
    const q = query(
      patientsRef,
      where('isDeleted', '==', false),
      where('nameNormalized', '>=', prefix),
      where('nameNormalized', '<', prefix + '\uf8ff'),
      orderBy('nameNormalized', 'asc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as Patient);
  },

  /**
   * Fichas cujo `nameNormalized` é EXATAMENTE um dos nomes informados (até
   * 30, limite do `in`). Usada pela sugestão de paciente na fila "Exames sem
   * paciente" com as ordens possíveis do nome que veio do aparelho
   * (`nameOrderVariants`).
   */
  async findByNormalizedNames(clinicId: string, names: string[]): Promise<Patient[]> {
    const list = [...new Set(names)].slice(0, 30);
    if (list.length === 0) return [];
    const q = query(
      collection(db, 'clinics', clinicId, 'patients'),
      where('nameNormalized', 'in', list),
      fsLimit(10),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as Patient).filter((p) => !p.isDeleted);
  },

  /**
   * Lista pacientes marcados com a tag "VIP" (case-sensitive como gravado).
   * Limit 100 — só preview pra filtro de chip, não substitui busca textual.
   */
  async listVipPatients(clinicId: string, limit = 100): Promise<Patient[]> {
    const patientsRef = collection(db, 'clinics', clinicId, 'patients');
    const q = query(
      patientsRef,
      where('isDeleted', '==', false),
      where('tags', 'array-contains', 'VIP'),
      fsLimit(limit),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as Patient);
  },

  /**
   * Lista pacientes com cadastro incompleto (registrationStatus === 'MINIMAL').
   * Útil pra triagem de qualidade de dados.
   */
  async listIncompletePatients(clinicId: string, limit = 100): Promise<Patient[]> {
    const patientsRef = collection(db, 'clinics', clinicId, 'patients');
    const q = query(
      patientsRef,
      where('isDeleted', '==', false),
      where('registrationStatus', '==', 'MINIMAL'),
      fsLimit(limit),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as Patient);
  },

  /**
   * Aniversariantes do mês informado (1-12).
   * Depende do campo denormalizado `birthDayMonth` ("MM-DD") populado em
   * create/update. Bases legadas precisam rodar `backfillBirthDayMonth`.
   */
  async listBirthdaysInMonth(
    clinicId: string,
    month: number,
    limit = 100,
  ): Promise<Patient[]> {
    if (month < 1 || month > 12) return [];
    const mm = String(month).padStart(2, '0');
    const patientsRef = collection(db, 'clinics', clinicId, 'patients');
    const q = query(
      patientsRef,
      where('isDeleted', '==', false),
      where('birthDayMonth', '>=', `${mm}-01`),
      where('birthDayMonth', '<=', `${mm}-31`),
      orderBy('birthDayMonth', 'asc'),
      fsLimit(limit),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as Patient);
  },

  /**
   * Últimos pacientes únicos com atendimento concluído (status CONCLUIDA),
   * ordenados pela data da consulta mais recente. Busca uma janela de
   * appointments maior que `limit` pra deduplicar — um mesmo paciente pode
   * ter várias consultas concluídas e só queremos a mais recente dele.
   *
   * Usa `date` (sempre presente) em vez de `attendanceEndedAt` pra abranger
   * também atendimentos legados marcados CONCLUIDA antes da feature do
   * cronômetro existir. Pra desempate de mesma data, secundariza por
   * `startTime` desc (último horário do dia primeiro).
   *
   * Requer índice composto em appointments (status ASC, date DESC).
   */
  async listRecentlyAttendedPatients(
    clinicId: string,
    limit = 50,
  ): Promise<Patient[]> {
    const appointmentsRef = collection(db, 'clinics', clinicId, 'appointments');
    // Janela 4x o limite — folga pra deduplicar pacientes que voltaram várias vezes.
    const windowSize = Math.max(limit * 4, 100);
    const q = query(
      appointmentsRef,
      where('status', '==', 'CONCLUIDA'),
      orderBy('date', 'desc'),
      fsLimit(windowSize),
    );
    const snap = await getDocs(q);
    const uniqueIds: string[] = [];
    const seen = new Set<string>();
    for (const docSnap of snap.docs) {
      const data = docSnap.data() as { patientId?: string; isDeleted?: boolean };
      if (!data.patientId || data.isDeleted) continue;
      if (seen.has(data.patientId)) continue;
      seen.add(data.patientId);
      uniqueIds.push(data.patientId);
      if (uniqueIds.length >= limit) break;
    }
    return this.getPatientsByIds(clinicId, uniqueIds);
  },

  /**
   * Pacientes com retorno recomendado em atraso (data passada).
   * `recommendedReturnAt > 0` filtra os que de fato têm retorno marcado.
   */
  async listOverdueReturnPatients(
    clinicId: string,
    limit = 100,
  ): Promise<Patient[]> {
    const now = Date.now();
    const patientsRef = collection(db, 'clinics', clinicId, 'patients');
    const q = query(
      patientsRef,
      where('isDeleted', '==', false),
      where('recommendedReturnAt', '>', 0),
      where('recommendedReturnAt', '<', now),
      orderBy('recommendedReturnAt', 'asc'),
      fsLimit(limit),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as Patient);
  },

  /**
   * Backfill do campo `birthDayMonth` (MM-DD) em pacientes legados.
   * Necessário antes do filtro de aniversariantes funcionar em bases antigas.
   */
  async backfillBirthDayMonth(
    clinicId: string,
    onProgress?: (processed: number, updated: number) => void,
  ): Promise<{ processed: number; updated: number }> {
    const patientsRef = collection(db, 'clinics', clinicId, 'patients');
    const PAGE = 500;
    let processed = 0;
    let updated = 0;
    let lastId: string | null = null;
    while (true) {
      const pageQuery: Query<DocumentData> = lastId
        ? query(patientsRef, orderBy('id'), where('id', '>', lastId), fsLimit(PAGE))
        : query(patientsRef, orderBy('id'), fsLimit(PAGE));
      const snap = await getDocs(pageQuery);
      if (snap.empty) break;
      const batch = writeBatch(db);
      let batchOps = 0;
      for (const docSnap of snap.docs) {
        processed++;
        lastId = docSnap.id;
        const data = docSnap.data() as Patient;
        if (data.birthDayMonth) continue;
        const dm = extractBirthDayMonth(data.birthDate);
        if (!dm) continue;
        batch.update(doc(db, 'clinics', clinicId, 'patients', docSnap.id), {
          birthDayMonth: dm,
        });
        batchOps++;
        updated++;
      }
      if (batchOps > 0) await batch.commit();
      onProgress?.(processed, updated);
      if (snap.size < PAGE) break;
    }
    return { processed, updated };
  },

  /**
   * Backfill one-shot do `cpfHash` — PRÉ-REQUISITO do vínculo de prontuário
   * pelo Portal do Paciente.
   *
   * O problema que ele resolve: a base migrada do HiDoctor tem o CPF em
   * claro no `patientNumber` (createPatient grava os 11 dígitos ali), mas
   * muitas fichas nunca ganharam `cpfHash` — e é `cpfHash` que o portal
   * consulta. O cura-preguiçoso `healPatientNumbers` NÃO resolve esses
   * casos: ele faz early-return justamente quando `patientNumber` já tem
   * 11 dígitos. Resultado: o paciente antigo digita o CPF certo no portal,
   * não é encontrado, e (com o auto-cadastro ligado) criaria uma segunda
   * ficha — órfã do histórico inteiro dele.
   *
   * Fonte do hash em ordem de preferência:
   *   1. `patientNumber` com 11 dígitos (em claro, sem custo);
   *   2. `cpf` cifrado, decifrado aqui (mais lento, e só funciona no
   *      browser porque a chave é derivada no cliente).
   */
  async backfillCpfHash(
    clinicId: string,
    onProgress?: (processed: number, updated: number) => void,
  ): Promise<{ processed: number; updated: number; skippedNoCpf: number }> {
    const patientsRef = collection(db, 'clinics', clinicId, 'patients');
    const PAGE = 500;

    let processed = 0;
    let updated = 0;
    let skippedNoCpf = 0;
    let lastId: string | null = null;

    while (true) {
      const pageQuery: Query<DocumentData> = lastId
        ? query(patientsRef, orderBy('id'), where('id', '>', lastId), fsLimit(PAGE))
        : query(patientsRef, orderBy('id'), fsLimit(PAGE));
      const snap = await getDocs(pageQuery);
      if (snap.empty) break;

      const batch = writeBatch(db);
      let batchOps = 0;

      for (const docSnap of snap.docs) {
        processed++;
        lastId = docSnap.id;
        const data = docSnap.data() as Patient;
        if (data.cpfHash) continue; // já indexado

        let plain = '';
        const pn = (data.patientNumber || '').replace(/\D/g, '');
        if (/^\d{11}$/.test(pn)) {
          plain = pn;
        } else if (data.cpf) {
          try {
            plain = (await decryptData(data.cpf)).replace(/\D/g, '');
          } catch {
            plain = '';
          }
        }
        if (!/^\d{11}$/.test(plain)) {
          skippedNoCpf++;
          continue;
        }

        const updates: Record<string, unknown> = { cpfHash: await hashData(plain) };
        // Aproveita a passada pra completar o prefixo, que usa a mesma fonte.
        if (!data.cpfPrefix) updates.cpfPrefix = plain.slice(0, CPF_PREFIX_LENGTH);

        batch.update(doc(db, 'clinics', clinicId, 'patients', docSnap.id), updates);
        batchOps++;
        updated++;
      }

      if (batchOps > 0) await batch.commit();
      onProgress?.(processed, updated);

      if (snap.size < PAGE) break;
    }

    return { processed, updated, skippedNoCpf };
  },

  /**
   * Backfill one-shot do `phoneDigits` — telefone canônico derivado do
   * campo livre `phone`. Sem ele, o vínculo por telefone no portal só
   * funciona pra paciente cadastrado depois desta versão.
   *
   * Mesmo padrão do backfillCpfPrefix: páginas determinísticas por id,
   * writeBatch, idempotente.
   *
   * `skippedNoDdd` conta as fichas cujo telefone existe mas NÃO tem DDD
   * (8 ou 9 dígitos — herança do HiDoctor, que era sistema local). Essas
   * são irrecuperáveis sem chutar o DDD, e chutar significa arriscar
   * vincular o paciente errado. Ficam sem o campo, de propósito.
   */
  async backfillPhoneDigits(
    clinicId: string,
    onProgress?: (processed: number, updated: number) => void,
  ): Promise<{ processed: number; updated: number; skippedNoDdd: number }> {
    const patientsRef = collection(db, 'clinics', clinicId, 'patients');
    const PAGE = 500;

    let processed = 0;
    let updated = 0;
    let skippedNoDdd = 0;
    let lastId: string | null = null;

    while (true) {
      const pageQuery: Query<DocumentData> = lastId
        ? query(patientsRef, orderBy('id'), where('id', '>', lastId), fsLimit(PAGE))
        : query(patientsRef, orderBy('id'), fsLimit(PAGE));
      const snap = await getDocs(pageQuery);
      if (snap.empty) break;

      const batch = writeBatch(db);
      let batchOps = 0;

      for (const docSnap of snap.docs) {
        processed++;
        lastId = docSnap.id;
        const data = docSnap.data() as Patient;
        if (data.phoneDigits) continue; // já indexado
        if (!data.phone) continue;
        const digits = normalizePhoneDigits(data.phone);
        if (!digits) {
          // Tem telefone escrito, mas não dá pra usar (sem DDD, ou só texto).
          if (data.phone.replace(/\D/g, '').length > 0) skippedNoDdd++;
          continue;
        }
        batch.update(doc(db, 'clinics', clinicId, 'patients', docSnap.id), {
          phoneDigits: digits,
        });
        batchOps++;
        updated++;
      }

      if (batchOps > 0) await batch.commit();
      onProgress?.(processed, updated);

      if (snap.size < PAGE) break;
    }

    return { processed, updated, skippedNoDdd };
  },

  /**
   * Backfill one-shot: percorre todos os pacientes da clinica e preenche
   * o campo cpfPrefix nos que ainda nao tem. Necessario pra ativar a busca
   * por primeiros digitos em bases legadas.
   *
   * Usa paginas de ate 500 escritas por writeBatch. Retorna estatisticas.
   */
  async backfillCpfPrefix(
    clinicId: string,
    onProgress?: (processed: number, updated: number) => void,
  ): Promise<{ processed: number; updated: number }> {
    const patientsRef = collection(db, 'clinics', clinicId, 'patients');
    const PAGE = 500;

    let processed = 0;
    let updated = 0;
    let lastId: string | null = null;

    // Percorre paginando por ID (doc id) pra ser determinista
    // e nao depender de nenhum indice extra
    while (true) {
      const pageQuery: Query<DocumentData> = lastId
        ? query(patientsRef, orderBy('id'), where('id', '>', lastId), fsLimit(PAGE))
        : query(patientsRef, orderBy('id'), fsLimit(PAGE));
      const snap = await getDocs(pageQuery);
      if (snap.empty) break;

      const batch = writeBatch(db);
      let batchOps = 0;

      for (const docSnap of snap.docs) {
        processed++;
        lastId = docSnap.id;
        const data = docSnap.data() as Patient & { cpfPrefix?: string };
        if (data.cpfPrefix) continue; // ja indexado
        if (!data.cpf) continue; // sem CPF pra indexar
        try {
          const plain = await decryptData(data.cpf);
          const prefix = extractCpfPrefix(plain);
          if (!prefix) continue;
          batch.update(doc(db, 'clinics', clinicId, 'patients', docSnap.id), {
            cpfPrefix: prefix,
          });
          batchOps++;
          updated++;
        } catch {
          // CPF nao descriptografavel — pula
        }
      }

      if (batchOps > 0) await batch.commit();
      onProgress?.(processed, updated);

      if (snap.size < PAGE) break;
    }

    return { processed, updated };
  },

  /**
   * T143 (FR-143): Check for duplicate patients.
   * Returns each match tagged with the reason it matched ('cpf' | 'phone' | 'name').
   * Name matches are EXACT (after accent/case normalization) — homônimos only.
   */
  async checkDuplicates(
    clinicId: string,
    name: string,
    phone?: string,
    cpf?: string,
  ): Promise<Array<{ patient: Patient; reason: 'cpf' | 'phone' | 'name' }>> {
    const duplicates: Array<{ patient: Patient; reason: 'cpf' | 'phone' | 'name' }> = [];
    const patientsRef = collection(db, 'clinics', clinicId, 'patients');

    // Check by CPF (exact via hash)
    if (cpf) {
      const hash = await hashData(cpf);
      const q = query(patientsRef, where('cpfHash', '==', hash), where('isDeleted', '==', false));
      const snap = await getDocs(q);
      duplicates.push(
        ...snap.docs.map((d) => ({ patient: d.data() as Patient, reason: 'cpf' as const })),
      );
    }

    // Check by phone (exact) — but only treat as duplicate if the name is
    // reasonably similar, since family members often share a phone number.
    if (phone && duplicates.length === 0) {
      const digits = phone.replace(/\D/g, '');
      const q = query(patientsRef, where('phone', '==', digits), where('isDeleted', '==', false));
      const snap = await getDocs(q);
      const normalizedInput = name ? normalizeForSearch(name) : '';
      const phoneMatches = snap.docs
        .map((d) => d.data() as Patient)
        .filter((p) => {
          if (!normalizedInput) return true;
          const pNorm = normalizeForSearch(p.name);
          return stringSimilarity(pNorm, normalizedInput) >= 0.6;
        });
      duplicates.push(...phoneMatches.map((p) => ({ patient: p, reason: 'phone' as const })));
    }

    // Check by EXACT normalized name (homônimos) if no other match found.
    if (duplicates.length === 0 && name.length >= 3) {
      const normalized = normalizeForSearch(name);
      const q = query(
        patientsRef,
        where('isDeleted', '==', false),
        where('nameNormalized', '==', normalized),
      );
      const snap = await getDocs(q);
      duplicates.push(
        ...snap.docs.map((d) => ({ patient: d.data() as Patient, reason: 'name' as const })),
      );
    }

    return duplicates;
  },

  /**
   * Upload a compressed ID document photo to Storage and attach it to the patient.
   *
   * Path: patients/{clinicId}/{patientId}/id-documents/{timestamp}_{safeName}
   * Returns the full PatientIdDocument record (with download URL).
   *
   * Call this AFTER createPatient so the patientId exists. For the initial
   * registration flow, pass `skipAttach=true` to upload without touching
   * the Firestore doc (useful when the patientId is brand-new and you're
   * already building the initial payload client-side).
   */
  async uploadIdDocument(
    clinicId: string,
    patientId: string,
    file: File,
    type: PatientIdDocument['type'] = 'OUTRO',
    opts: { ocrExtracted?: boolean; skipAttach?: boolean } = {},
  ): Promise<PatientIdDocument> {
    const timestamp = Date.now();
    const safeName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    // Caminho dentro de clinics/{clinicId}/** — único escopo onde as
    // Storage Rules autorizam escrita por membros da clínica. Antes era
    // patients/{clinicId}/... e o Storage rejeitava com retry-limit-exceeded.
    // Documentos antigos eventualmente salvos no caminho legado continuam
    // acessíveis via download token; deletes derivam o path da URL.
    const path = `clinics/${clinicId}/patients/${patientId}/id-documents/${safeName}`;
    const fileRef = storageRef(storage, path);

    await uploadBytes(fileRef, file, {
      contentType: file.type || 'image/jpeg',
      customMetadata: { type },
    });
    const url = await getDownloadURL(fileRef);

    const document: PatientIdDocument = {
      id: `doc_${timestamp}_${Math.random().toString(36).slice(2, 7)}`,
      type,
      url,
      fileName: file.name,
      uploadedAt: timestamp,
      ocrExtracted: opts.ocrExtracted ?? false,
    };

    if (!opts.skipAttach) {
      const docRef = doc(db, 'clinics', clinicId, 'patients', patientId);
      await updateDoc(docRef, {
        idDocuments: arrayUnion(document),
        updatedAt: serverTimestamp(),
      });
    }

    return document;
  },

  /**
   * Remove a patient ID document (Storage file + Firestore array entry).
   */
  async removeIdDocument(
    clinicId: string,
    patientId: string,
    document: PatientIdDocument,
  ): Promise<void> {
    // Best-effort Storage cleanup — we derive the path from the URL token.
    try {
      // Firebase Storage download URLs embed the path as /o/<encoded-path>?alt=media&token=...
      const match = document.url.match(/\/o\/([^?]+)/);
      if (match) {
        const path = decodeURIComponent(match[1]);
        await deleteObject(storageRef(storage, path));
      }
    } catch {
      // File may already be gone — don't block the Firestore update
    }

    const docRef = doc(db, 'clinics', clinicId, 'patients', patientId);
    await updateDoc(docRef, {
      idDocuments: arrayRemove(document),
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * T081: Record LGPD consent.
   */
  async recordLgpdConsent(
    clinicId: string,
    patientId: string,
    recordedBy: string,
    consentVersion: string,
  ) {
    const consentsRef = collection(db, 'clinics', clinicId, 'patients', patientId, 'consents');
    await addDoc(consentsRef, {
      patientId,
      consentVersion,
      recordedBy,
      consentedAt: serverTimestamp(),
      type: 'LGPD_GENERAL',
    });

    // Update patient with latest consent info
    const patientRef = doc(db, 'clinics', clinicId, 'patients', patientId);
    await updateDoc(patientRef, {
      lgpdConsentDate: serverTimestamp(),
      lgpdConsentVersion: consentVersion,
      updatedAt: serverTimestamp(),
    });
  },
};

/**
 * Simple string similarity (Sørensen–Dice coefficient).
 */
function stringSimilarity(a: string, b: string): number {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;

  const bigrams = new Map<string, number>();
  for (let i = 0; i < a.length - 1; i++) {
    const bigram = a.substring(i, i + 2);
    bigrams.set(bigram, (bigrams.get(bigram) || 0) + 1);
  }

  let intersections = 0;
  for (let i = 0; i < b.length - 1; i++) {
    const bigram = b.substring(i, i + 2);
    const count = bigrams.get(bigram) || 0;
    if (count > 0) {
      bigrams.set(bigram, count - 1);
      intersections++;
    }
  }

  return (2 * intersections) / (a.length - 1 + (b.length - 1));
}
